<template>
	<div>
		<h1>Поставщик</h1>

		<div class="form-section">
			<h3>{{ form.id ? 'Редактировать' : 'Добавить' }} поставщика</h3>
			<div class="form-row">
				<PrimeInputText v-model="form.name" placeholder="Название поставщика" />
				<PrimeInputText v-model="form.email" type="email" placeholder="Email адрес" />
				<PrimeButton label="Сохранить" icon="pi pi-check" @click="save" />
				<PrimeButton v-if="form.id" label="Отмена" icon="pi pi-times" severity="secondary" @click="cancelEdit" />
			</div>
		</div>

		<PrimeDataTable
			:value="suppliers"
			v-model:editingRows="editingSuppliers"
			editMode="row"
			dataKey="id"
			stripedRows
			showGridlines
			size="small"
			@row-edit-save="onRowEditSave"
		>
			<PrimeColumn header="(#)" :style="{ width: '1%', whiteSpace: 'nowrap' }">
				<template #body="slotProps">
					{{ slotProps.index + 1 }}
				</template>
			</PrimeColumn>

			<PrimeColumn field="name" header="Название">
				<template #editor="{ data, field }">
					<PrimeInputText v-model="data[field]" />
				</template>
			</PrimeColumn>

			<PrimeColumn field="email" header="Email">
				<template #editor="{ data, field }">
					<PrimeInputText v-model="data[field]" type="email" />
				</template>
			</PrimeColumn>

			<PrimeColumn
				:rowEditor="true"
				:style="{ width: '1%', whiteSpace: 'nowrap' }"
				bodyStyle="text-align:center"
			/>

			<PrimeColumn :exportable="false" :style="{ width: '1%', whiteSpace: 'nowrap' }">
				<template #body="slotProps">
					<PrimeButton icon="pi pi-trash" variant="outlined" severity="danger" @click="confirmDeleteSupplier(slotProps.data)" />
				</template>
			</PrimeColumn>
		</PrimeDataTable>

		<PrimeDialog v-model:visible="deleteSupplierDialog" :style="{ width: '450px' }" header="Подтверждение удаления" :modal="true">
			<div class="flex items-center gap-4">
				<i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #f44336;"></i>
				<span v-if="supplier">
					Вы уверены, что хотите удалить <b>{{ supplier.name }}</b>?
				</span>
			</div>
			<template #footer>
				<PrimeButton label="Нет" icon="pi pi-times" text @click="deleteSupplierDialog = false" severity="secondary" variant="text" />
				<PrimeButton label="Да" icon="pi pi-check" @click="deleteSupplier" severity="danger" />
			</template>
		</PrimeDialog>
	</div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { api } from "../api";
import { useToast } from 'primevue/usetoast';

const suppliers = ref([]);
const editingSuppliers = ref([]);
const form = ref({ id: 0, name: "", email: "" });
const deleteSupplierDialog = ref(false);
const toast = useToast();
const supplier = ref({});

// Load data
const load = () => api.get("/suppliers").then(r => suppliers.value = r.data);
onMounted(load);

// Email validation
const isValidEmail = (email) => {
	const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return re.test(email);
};

// Save
const save = () => {
	if (!form.value.name || !form.value.email) {
		toast.add({ severity: 'warn', summary: 'Проверка', detail: 'Название и Email обязательны', life: 3000 });
		return;
	}

	if (!isValidEmail(form.value.email)) {
		toast.add({ severity: 'warn', summary: 'Проверка', detail: 'Пожалуйста, введите корректный email адрес', life: 3000 });
		return;
	}

	const promise = !form.value.id 
		? api.post("/suppliers", form.value)
		: api.put(`/suppliers/${form.value.id}`, form.value);
	
	promise.then(() => {
		afterSave();
		toast.add({ severity: 'success', summary: 'Успешно', detail: 'Поставщик сохранен', life: 3000 });
	}).catch(err => {
		toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось сохранить поставщика', life: 3000 });
		console.error(err);
	});
};

const afterSave = () => {
	form.value = { id: 0, name: "", email: "" };
	load();
};

const cancelEdit = () => {
	form.value = { id: 0, name: "", email: "" };
};

// PrimeVue row edit save
const onRowEditSave = (event) => {
	const { newData } = event;
	
	if (!isValidEmail(newData.email)) {
		toast.add({ severity: 'warn', summary: 'Проверка', detail: 'Пожалуйста, введите корректный email адрес', life: 3000 });
		load(); // Reload to cancel edit
		return;
	}

	api.put(`/suppliers/${newData.id}`, newData)
		.then(() => {
			load();
			toast.add({ severity: 'success', summary: 'Успешно', detail: 'Поставщик обновлен', life: 3000 });
		})
		.catch(err => {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось обновить поставщика', life: 3000 });
			console.error(err);
			load(); // Reload on error
		});
};

const confirmDeleteSupplier = (s) => {
	supplier.value = s;
	deleteSupplierDialog.value = true;
};

const deleteSupplier = () => {
	api.delete(`/suppliers/${supplier.value.id}`)
		.then(() => {
			load();
			deleteSupplierDialog.value = false;
			supplier.value = {};
			toast.add({ severity: 'success', summary: 'Успешно', detail: 'Поставщик удален', life: 3000 });
		})
		.catch(err => {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить поставщика', life: 3000 });
			console.error(err);
		});
};
</script>

<style scoped>
.form-section {
	background: white;
	padding: 1.5rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	margin-bottom: 2rem;
}

.form-section h3 {
	margin-bottom: 1rem;
	color: #2c3e50;
}

.form-row {
	display: flex;
	gap: 0.5rem;
	align-items: center;
	flex-wrap: wrap;
}

.form-row input {
	flex: 1;
	min-width: 200px;
}
</style>

